import { useState, useEffect, useRef } from 'react';
import { 
  FileText, 
  Edit2, 
  Save, 
  X, 
  Loader,
  AlertCircle,
  Lock,
  LogOut,
  Eye,
  Bold,
  Italic,
  Code,
  List,
  Heading,
  Link as LinkIcon,
  Image as ImageIcon,
  Quote,
  CheckCircle,
  Sparkles,
  Clock,
  User
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const validPasswords = ['witcher', 'abhi'];

function ProjectAnalysis() {
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editVersion, setEditVersion] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);

  // Check authentication on mount
  useEffect(() => {
    const auth = localStorage.getItem('projectAnalysisAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch analysis
  useEffect(() => {
    fetchAnalysis();
  }, []);

  const fetchAnalysis = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await fetch(`${API_URL}/project-analysis`);
      if (!response.ok) throw new Error('Failed to fetch analysis');
      const data = await response.json();
      setAnalysis(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (validPasswords.includes(loginPassword)) {
      setIsAuthenticated(true);
      localStorage.setItem('projectAnalysisAuth', 'true');
      setShowLoginModal(false);
      setLoginPassword('');
      setLoginError('');
    } else {
      setLoginError('Invalid password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('projectAnalysisAuth');
    setIsEditing(false);
  };

  const handleEdit = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    setEditContent(analysis?.content || '');
    setEditTitle(analysis?.title || '');
    setEditVersion(analysis?.version || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError('');
      const response = await fetch(`${API_URL}/project-analysis`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: editContent,
          title: editTitle,
          version: editVersion,
          updatedBy: 'Admin'
        })
      });
      if (!response.ok) throw new Error('Failed to save');
      const data = await response.json();
      // Backend now returns analysis object directly
      setAnalysis(data);
      setIsEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditContent('');
  };

  // Markdown formatting helpers
  const insertAtCursor = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editContent;
    const selectedText = text.substring(start, end);
    
    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    setEditContent(newText);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
    }, 0);
  };

  const formatBold = () => insertAtCursor('**', '**');
  const formatItalic = () => insertAtCursor('*', '*');
  const formatCode = () => insertAtCursor('`', '`');
  const formatHeading = () => insertAtCursor('## ', '');
  const formatList = () => insertAtCursor('- ', '');
  const formatQuote = () => insertAtCursor('> ', '');
  const formatLink = () => insertAtCursor('[', '](url)');
  const formatImage = () => insertAtCursor('![alt](', ')');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading project analysis...</p>
        </div>
      </div>
    );
  }

  if (error && !analysis) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchAnalysis}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-all duration-200 font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Elegant Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-3 rounded-xl shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{analysis?.title || 'Project Analysis'}</h1>
                <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {analysis?.version || 'v1.0'}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {analysis?.updatedBy || 'Admin'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {!isEditing ? (
                <>
                  <button
                    onClick={handleEdit}
                    className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl hover:bg-indigo-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  {isAuthenticated && (
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-4 py-2.5 rounded-xl hover:bg-gray-100 transition-all duration-200"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-200 font-medium ${
                      showPreview
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2 bg-green-600 text-white px-5 py-2.5 rounded-xl hover:bg-green-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg disabled:opacity-50"
                  >
                    {isSaving ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-4 py-2.5 rounded-xl hover:bg-gray-100 transition-all duration-200"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!isEditing ? (
          // View Mode - Beautiful Card Layout
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <div className="p-8 lg:p-12">
              <div className="prose prose-lg prose-indigo max-w-none">
                <ReactMarkdown
                  components={{
                    h1: (props) => (
                      <h1 className="text-4xl font-bold text-gray-900 mb-6 pb-4 border-b-2 border-indigo-200" {...props} />
                    ),
                    h2: (props) => (
                      <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-3" {...props}>
                        <Sparkles className="w-6 h-6 text-indigo-500" />
                        {props.children}
                      </h2>
                    ),
                    h3: (props) => (
                      <h3 className="text-2xl font-semibold text-gray-800 mt-6 mb-3" {...props} />
                    ),
                    ul: (props) => (
                      <ul className="space-y-2 my-4" {...props} />
                    ),
                    li: (props) => (
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span {...props} />
                      </li>
                    ),
                    code: ({ inline, ...props }) => 
                      inline ? (
                        <code className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-mono text-sm" {...props} />
                      ) : (
                        <code className="block bg-gray-900 text-gray-100 p-4 rounded-xl overflow-x-auto font-mono text-sm" {...props} />
                      ),
                    blockquote: (props) => (
                      <blockquote className="border-l-4 border-indigo-500 bg-indigo-50 pl-4 py-2 my-4 italic text-gray-700" {...props} />
                    ),
                    a: (props) => (
                      <a className="text-indigo-600 hover:text-indigo-700 underline decoration-2 decoration-indigo-300 hover:decoration-indigo-500 transition-colors" {...props} />
                    ),
                  }}
                >
                  {analysis?.content || '*No content available*'}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        ) : (
          // Edit Mode - Sleek Editor
          <div className="space-y-6">
            {/* Metadata Card */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                    placeholder="Project Completion Analysis"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
                  <input
                    type="text"
                    value={editVersion}
                    onChange={(e) => setEditVersion(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                    placeholder="1.0.0"
                  />
                </div>
              </div>
            </div>

            {/* Editor Card */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              {/* Simplified Toolbar */}
              <div className="bg-gray-50/80 border-b border-gray-200 p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={formatBold} title="Bold" className="p-2 rounded-lg hover:bg-white transition-colors">
                    <Bold className="w-4 h-4 text-gray-700" />
                  </button>
                  <button onClick={formatItalic} title="Italic" className="p-2 rounded-lg hover:bg-white transition-colors">
                    <Italic className="w-4 h-4 text-gray-700" />
                  </button>
                  <button onClick={formatCode} title="Code" className="p-2 rounded-lg hover:bg-white transition-colors">
                    <Code className="w-4 h-4 text-gray-700" />
                  </button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <button onClick={formatHeading} title="Heading" className="p-2 rounded-lg hover:bg-white transition-colors">
                    <Heading className="w-4 h-4 text-gray-700" />
                  </button>
                  <button onClick={formatList} title="List" className="p-2 rounded-lg hover:bg-white transition-colors">
                    <List className="w-4 h-4 text-gray-700" />
                  </button>
                  <button onClick={formatQuote} title="Quote" className="p-2 rounded-lg hover:bg-white transition-colors">
                    <Quote className="w-4 h-4 text-gray-700" />
                  </button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <button onClick={formatLink} title="Link" className="p-2 rounded-lg hover:bg-white transition-colors">
                    <LinkIcon className="w-4 h-4 text-gray-700" />
                  </button>
                  <button onClick={formatImage} title="Image" className="p-2 rounded-lg hover:bg-white transition-colors">
                    <ImageIcon className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
              </div>

              {/* Editor Area */}
              <div className={`grid ${showPreview ? 'grid-cols-1 lg:grid-cols-2 divide-x divide-gray-200' : 'grid-cols-1'}`}>
                <div className="p-6">
                  <textarea
                    ref={textareaRef}
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full h-[600px] px-4 py-3 border-0 rounded-lg focus:ring-0 outline-none font-mono text-sm leading-relaxed resize-none bg-transparent"
                    placeholder="# Start writing here...\n\nUse markdown for formatting!"
                  />
                  <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                    <span>{editContent.length} characters</span>
                    <span>{editContent.split('\n').length} lines</span>
                  </div>
                </div>

                {showPreview && (
                  <div className="p-6 bg-gray-50/50">
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown>{editContent || '*Start typing to see preview...*'}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Elegant Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-100 p-3 rounded-xl">
                  <Lock className="w-6 h-6 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Authentication Required</h2>
              </div>
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  setLoginPassword('');
                  setLoginError('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              Enter your password to edit the project documentation.
            </p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                  placeholder="Enter your password"
                  autoFocus
                />
              </div>

              {loginError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{loginError}</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-indigo-600 text-white py-3 rounded-xl hover:bg-indigo-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
              >
                Login
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectAnalysis;
